import { useDisclosure, Flex, MenuItem, Box } from '@chakra-ui/react';
import { ArchivedCompositionGroupsModal } from '@texas/components/article/variant/compositions/shared/group/ArchivedCompositionGroupsModal';
import { CompositionLocationData } from '@texas/components/article/variant/compositions/shared/hooks/useCompositionGroup';
import { OptionsList } from '@texas/components/article/variant/options/shared/OptionsList';
import { OptionsListContainer } from '@texas/components/article/variant/options/shared/OptionsListContainer';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { Icons, defaultIconSize } from '@texas/components/shared/Icons';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTemplatesOverview } from './useTemplatesOverview';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { TemplateCompositionGroupContainer } from './TemplateCompositionGroupContainer';

export function VdcTemplates() {
  const { t } = useTranslation();
  const options = useTemplatesOverview();

  const {
    createGroupRequest,
    versionsLoading,
    versions,
    archiveGroupRequest,
    isCreating,
    copyGroup,
    locationData,
  } = options;
  const {
    isOpen: isNewGroupOpen,
    onClose: onNewGroupClose,
    onOpen: onNewGroupOpen,
  } = useDisclosure();
  const {
    value: archivedGroupsLocationValue,
    isOpen: isShowArchivedOpen,
    onClose: onShowArchivedClose,
    onOpen: onShowArchivedOpen,
  } = useValueDisclosure<CompositionLocationData>();
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  useEffect(() => {
    if (selectedOption === null && versions && versions.length > 0)
      setSelectedOption(versions[0].id);
  }, [versions, selectedOption, setSelectedOption]);

  const option = versions?.find((x) => x.id === selectedOption);

  return (
    <ConfigurationPage
      title={t('configuration.vdcTemplates')}
      editorSurface={true}
    >
      <Flex pt={4} gap={2} px={2}>
        <OptionsListContainer
          menuItems={
            <>
              <MenuItem
                icon={<Icons.Plus boxSize={defaultIconSize} />}
                onClick={() => {
                  if (versions?.length === 0 && !versionsLoading) {
                    createGroupRequest();
                    return;
                  }

                  onNewGroupOpen();
                }}
              >
                {t('compositionGroup.option.addNew')}
              </MenuItem>
              <MenuItem
                onClick={() =>
                  onShowArchivedOpen({
                    template: locationData.current.template,
                    variantId: undefined,
                  })
                }
                icon={<Icons.ArchiveOutline boxSize={defaultIconSize} />}
              >
                {t('compositionGroup.option.archivedOptions')}
              </MenuItem>
            </>
          }
        >
          {isCreating && (
            <SpinnerWithLabel label={t('compositionGroup.option.loading')} />
          )}
          <OptionsList
            groupsLoading={versionsLoading}
            isCreating={isCreating}
            view="list"
            onCreateClick={() => {
              if (isCreating) return;
              createGroupRequest();
            }}
            groups={versions?.map((x) => ({
              id: x.id,
              fileId: x.fileId,
              version: x.version,
              compositions: x.compositions,
              note: x.note,
              isReady: x.isReady,
            }))}
            selectedOption={selectedOption}
            onClick={setSelectedOption}
          />
        </OptionsListContainer>

        {option && (
          <Box px={4} pb={4} pl={8} key={option.id} w="full">
            <TemplateCompositionGroupContainer
              option={option}
              onArchive={async () => await archiveGroupRequest(option.id)}
              onCopy={async (fromId) => await copyGroup({ fromId })}
            />
          </Box>
        )}
      </Flex>
      <VerifyDialogWithFunction
        headerTitle={t('compositionGroup.option.createNew')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        toPerform={() => createGroupRequest()}
        isOpen={isNewGroupOpen}
        onClose={onNewGroupClose}
      >
        {t('composition.newOptionConfirm')}
      </VerifyDialogWithFunction>
      {archivedGroupsLocationValue && (
        <ArchivedCompositionGroupsModal
          locationData={archivedGroupsLocationValue}
          isOpen={isShowArchivedOpen}
          onClose={onShowArchivedClose}
        />
      )}
    </ConfigurationPage>
  );
}
